import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";
import { Box, Divider, Typography } from "@mui/material";
import {
  CalendarPlusFill,
  ClockFill,
  PersonBadgeFill,
  GeoAltFill,
  TagFill,
  ImageFill,
  BookFill,
  CalendarDateFill,
  PersonVcardFill,
  XCircle,
} from "react-bootstrap-icons";

// Reusable SectionHeader component
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };
const positiveWholeNumberMessage =
  "maxParticipants must be a positive whole number";

const getApiErrorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (Array.isArray(data?.errors)) {
    return data.errors
      .map((item) => item?.message || item)
      .filter(Boolean)
      .join("\n");
  }
  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().filter(Boolean).join("\n");
  }
  return fallback;
};

const AddClassOffcanvas = ({ show, handleClose, onSaved }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    clearErrors,
  } = useForm();

  const [instructors, setInstructors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [noInstructorsAvailable, setNoInstructorsAvailable] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const startDate = watch("startDate");
  const selectedLocation = watch("location");

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      setIsLoading(true);
      try {
        const locationsRes = await axios.get(
          `${API_BASE_URL}/api/location/admin/getAllLocations`,
        );

        const activeLocations = (locationsRes.data?.data || []).filter(
          (location) => location.status === "Active",
        );

        setLocations(activeLocations);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load locations");
      } finally {
        setIsLoading(false);
      }
    };

    if (show) fetchData();
  }, [show]);

  useEffect(() => {
    const fetchInstructorsByLocation = async () => {
      if (!selectedLocation) {
        setInstructors([]);
        setNoInstructorsAvailable(false);
        return;
      }

      setIsLoading(true);
      setNoInstructorsAvailable(false);

      try {
        const token = localStorage.getItem("adminToken");

        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/instructor/getByLocation/${selectedLocation}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (response.data?.data?.length > 0) {
            setInstructors(response.data.data);
            setNoInstructorsAvailable(false);
          } else {
            setNoInstructorsAvailable(true);
            setInstructors([]);
          }
          return;
        } catch (apiError) {
          console.log("Location-specific API failed, trying fallback");
        }

        const allInstructorsRes = await axios.get(
          `${API_BASE_URL}/api/admin/getRegister/instructor`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const filtered =
          allInstructorsRes.data?.users?.filter(
            (inst) => inst.location && inst.location._id === selectedLocation,
          ) || [];

        if (filtered.length > 0) {
          setInstructors(filtered);
          setNoInstructorsAvailable(false);
        } else {
          setNoInstructorsAvailable(true);
          setInstructors([]);
        }
      } catch (error) {
        console.error("Error fetching instructors:", error);
        toast.error("Failed to load instructors");
        setInstructors([]);
        setNoInstructorsAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchInstructorsByLocation();
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedLocation]);

  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    const startTime = `${data.startTimeHour}:${data.startTimeMinute}`;
    const endTime = `${data.endTimeHour}:${data.endTimeMinute}`;

    const start = new Date(`01/01/2000 ${startTime}`);
    const end = new Date(`01/01/2000 ${endTime}`);
    if (end <= start) {
      toast.error("End time must be after start time");
      return;
    }

    formData.append("title", data.title);
    formData.append("theme", data.theme);
    formData.append("startDate", data.startDate);
    formData.append("endDate", data.endDate);
    formData.append("sessionType", data.sessionType);
    formData.append("startTime", startTime);
    formData.append("endTime", endTime);
    formData.append("location", data.location);
    formData.append("Instructor", data.instructor);
    formData.append("Type", data.type);
    formData.append(
      "maxParticipants",
      data.maxParticipants?.trim() ? data.maxParticipants.trim() : "null",
    );

    if (data.image?.[0]) {
      formData.append("Image", data.image[0]);
    }

    tags.forEach((tag) => formData.append("tags", tag));

    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${API_BASE_URL}/api/AdminClasses/addClass`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Class added successfully");
      handleClose();
      reset();
      setTags([]);
      onSaved();
    } catch (err) {
      console.error("Error adding class:", err);
      toast.error(getApiErrorMessage(err, "Failed to add class"));
    }
  };

  const handleCloseOffcanvas = () => {
    reset();
    setTags([]);
    handleClose();
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleCloseOffcanvas}
      placement="end"
      backdrop="static"
      style={{ width: 550 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarPlusFill size={13} color="#ffeb3b" />
          </Box>
          Add New Class / Workshop
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        {isLoading && !locations.length ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="80vh"
          >
            <Spinner animation="border" style={{ color: "#0d47a1" }} />
          </Box>
        ) : (
          <Form
            onSubmit={handleSubmit(onSubmit)}
            className="p-3 rounded-3 shadow-sm bg-white"
          >
            <SectionHeader
              icon={<BookFill size={13} />}
              label="Basic Information"
            />

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Title <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  {...register("title", { required: "Title is required" })}
                  isInvalid={!!errors.title}
                  placeholder="Enter class title"
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Description/Theme <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  {...register("theme", { required: "Theme is required" })}
                  isInvalid={!!errors.theme}
                  placeholder="Enter class description"
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.theme?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  min={today}
                  onKeyDown={(e) => e.preventDefault()}
                  {...register("startDate", {
                    required: "Start date is required",
                    validate: (value) => {
                      const selectedDate = new Date(value);
                      const todayDate = new Date();
                      todayDate.setHours(0, 0, 0, 0);
                      return (
                        selectedDate >= todayDate ||
                        "Start date cannot be in the past"
                      );
                    },
                  })}
                  isInvalid={!!errors.startDate}
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startDate?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  min={startDate || today}
                  onKeyDown={(e) => e.preventDefault()}
                  {...register("endDate", {
                    required: "End date is required",
                    validate: (value) => {
                      if (!startDate) return true;
                      return (
                        new Date(value) >= new Date(startDate) ||
                        "End date cannot be before start date"
                      );
                    },
                  })}
                  isInvalid={!!errors.endDate}
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.endDate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <SectionHeader
              icon={<ClockFill size={13} />}
              label="Schedule Details"
            />

            <Row className="mb-3">
              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  Session Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  {...register("sessionType", {
                    required: "Session type is required",
                  })}
                  isInvalid={!!errors.sessionType}
                  style={fieldStyle}
                >
                  <option value="">Select Session Type</option>
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.sessionType?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  {...register("type", { required: "Type is required" })}
                  isInvalid={!!errors.type}
                  style={fieldStyle}
                >
                  <option value="">Select Type</option>
                  <option value="Regular Class">Regular Class</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Special Event">Special Event</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.type?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  Start Time <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    {...register("startTimeHour", {
                      required: "Start hour is required",
                    })}
                    isInvalid={!!errors.startTimeHour}
                    style={fieldStyle}
                  >
                    <option value="">Hour</option>
                    {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                      <option key={`start-hour-${hour}`} value={hour}>
                        {hour.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    {...register("startTimeMinute", {
                      required: "Start minute is required",
                    })}
                    isInvalid={!!errors.startTimeMinute}
                    style={fieldStyle}
                  >
                    <option value="">Min</option>
                    {Array.from({ length: 12 }, (_, i) =>
                      (i * 5).toString().padStart(2, "0"),
                    ).map((min) => (
                      <option key={`start-min-${min}`} value={min}>
                        {min}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {watch("startTimeHour") && watch("startTimeMinute") && (
                  <div className="mt-1 small" style={{ color: "#7b8ab8" }}>
                    Selected:{" "}
                    {watch("startTimeHour")?.toString().padStart(2, "0")}:
                    {watch("startTimeMinute")}
                  </div>
                )}
              </Form.Group>

              <Form.Group as={Col} md={6}>
                <Form.Label style={labelStyle}>
                  End Time <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    {...register("endTimeHour", {
                      required: "End hour is required",
                    })}
                    isInvalid={!!errors.endTimeHour}
                    style={fieldStyle}
                  >
                    <option value="">Hour</option>
                    {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                      <option key={`end-hour-${hour}`} value={hour}>
                        {hour.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    {...register("endTimeMinute", {
                      required: "End minute is required",
                    })}
                    isInvalid={!!errors.endTimeMinute}
                    style={fieldStyle}
                  >
                    <option value="">Min</option>
                    {Array.from({ length: 12 }, (_, i) =>
                      (i * 5).toString().padStart(2, "0"),
                    ).map((min) => (
                      <option key={`end-min-${min}`} value={min}>
                        {min}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {watch("endTimeHour") && watch("endTimeMinute") && (
                  <div className="mt-1 small" style={{ color: "#7b8ab8" }}>
                    Selected:{" "}
                    {watch("endTimeHour")?.toString().padStart(2, "0")}:
                    {watch("endTimeMinute")}
                  </div>
                )}
              </Form.Group>
            </Row>

            <SectionHeader
              icon={<PersonVcardFill size={13} />}
              label="Assignment"
            />

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Location <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  {...register("location", {
                    required: "Location is required",
                    onChange: (e) => {
                      setValue("instructor", "");
                      setNoInstructorsAvailable(false);
                      if (errors.instructor) clearErrors("instructor");
                    },
                  })}
                  isInvalid={!!errors.location}
                  style={fieldStyle}
                >
                  <option value="">Select Location</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.location}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.location?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Instructor <span className="text-danger">*</span>
                </Form.Label>
                {isLoading && selectedLocation ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ color: "#0d47a1" }}
                    />
                    <span style={{ color: "#7b8ab8" }}>
                      Loading instructors...
                    </span>
                  </Box>
                ) : noInstructorsAvailable ? (
                  <Alert
                    variant="warning"
                    className="py-2"
                    style={{
                      borderRadius: "8px",
                      border: "1.5px solid #ffd666",
                    }}
                  >
                    No instructors available for this location
                  </Alert>
                ) : (
                  <Form.Select
                    {...register("instructor", {
                      required: "Instructor is required",
                    })}
                    isInvalid={!!errors.instructor}
                    disabled={!selectedLocation || instructors.length === 0}
                    style={fieldStyle}
                  >
                    <option value="">
                      {selectedLocation
                        ? "Select Instructor"
                        : "Select a location first"}
                    </option>
                    {instructors.map((inst) => (
                      <option key={inst._id} value={inst._id}>
                        {inst.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.instructor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <SectionHeader
              icon={<TagFill size={13} />}
              label="Additional Details"
            />

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>Tags</Form.Label>
                <div className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag (e.g., Beginner, Yoga, Meditation)"
                    style={fieldStyle}
                    onKeyDown={(e) => e.key === "Enter" && handleAddTag(e)}
                  />
                  <Button
                    variant="primary"
                    className="ms-2"
                    onClick={handleAddTag}
                    style={{
                      background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add Tag
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="badge d-flex align-items-center"
                      style={{
                        background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                        borderRadius: "12px",
                        padding: "6px 12px",
                        fontWeight: 500,
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "0.5rem" }}
                        onClick={() => handleRemoveTag(tag)}
                        aria-label="Remove"
                      />
                    </span>
                  ))}
                </div>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Maximum Participants / Available Spots
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  placeholder="Leave blank for unlimited spots"
                  {...register("maxParticipants", {
                    validate: (value) =>
                      !value ||
                      /^[1-9]\d*$/.test(value) ||
                      positiveWholeNumberMessage,
                  })}
                  isInvalid={!!errors.maxParticipants}
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.maxParticipants?.message}
                </Form.Control.Feedback>
                <Form.Text muted>Leave empty for unlimited spots.</Form.Text>
              </Form.Group>
            </Row>

            <Row className="mb-4">
              <Form.Group as={Col} md={12}>
                <Form.Label style={labelStyle}>
                  Image <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  {...register("image", { required: "Image is required" })}
                  isInvalid={!!errors.image}
                  style={fieldStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.image?.message}
                </Form.Control.Feedback>
                <Form.Text muted>Upload a class image (JPG, PNG)</Form.Text>
              </Form.Group>
            </Row>

            <div className="d-grid gap-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="d-flex align-items-center justify-content-center gap-2 fw-bold"
                style={{
                  background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px",
                  boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" animation="border" /> Creating Class...
                  </>
                ) : (
                  <>
                    <CalendarPlusFill size={15} /> Create Class
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleCloseOffcanvas}
                className="fw-bold"
                style={{
                  border: "1.5px solid #dde4f5",
                  color: "#1a237e",
                  borderRadius: "10px",
                  padding: "12px",
                  background: "transparent",
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddClassOffcanvas;
